// What an agent request says and how it went, in words: shared by the inbox of agent requests
// (ApprovalsInbox.tsx, Riverstone and the Agents place) and Blueprint's Accounting › Agent requests
// (books/ApprovalsPage.tsx), which draws on ui-web's parts and so loads none of the inbox's stylesheets.
import { client } from '../api.ts';
import type { Approval } from '../books/types.ts';

export type ApprovalStatus = 'pending' | 'approved' | 'declined';
export type ApprovalPage = { approvals: Approval[]; total: number; next: string | null };

/** When it was asked, decided or expires: "Nov 4, 2026, 6:06 a.m.". */
export const requestWhen = (iso: string) =>
  new Date(iso).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' });

/** "agent:accounting" → "accounting". */
export const agentKeyOf = (ref: string) => ref.replace(/^agent:/, '');

/** What happened when an approved request ran (QA-261): it ran, it is still to run, or why it failed. */
export function runResult(outcome: unknown): { done: boolean; failed: string | null } {
  const o = outcome as { ok?: boolean; error?: { message?: string } } | null;
  if (!o || typeof o.ok !== 'boolean') return { done: false, failed: null };
  return o.ok ? { done: true, failed: null } : { done: true, failed: o.error?.message ?? 'it was refused' };
}

/** An approved request that was refused when it ran is not tried again (approvals.execute). */
export const NOT_AGAIN = 'It won’t run by itself later: ask the agent again, or do it yourself.';

/**
 * What the approver is told once an approved request has run (QA-859): it ran, or it didn't and why,
 * and that it won't run by itself later (a refused run is recorded, never retried). Null while it
 * hasn't run yet. `who` is who it runs as, with its article ("the office copilot", "you").
 */
export function approvedText(who: string, outcome: unknown): string | null {
  const ran = runResult(outcome);
  if (!ran.done) return null;
  return ran.failed
    ? `Approved, but it did not run: ${ran.failed.replace(/\.?$/, '.')} ${NOT_AGAIN}`
    : `Approved: it ran as ${who}.`;
}

/** "Approved by Demo Owner, Nov 3, 2026, 3:00 a.m.": what was decided, by whom, when (QA-858). */
export function decisionLine(
  a: Pick<Approval, 'status' | 'decidedByName' | 'decidedAt'>,
  when: (iso: string) => string,
): string {
  const word = a.status === 'approved' ? 'Approved' : a.status === 'declined' ? 'Declined' : 'Expired';
  const by = a.status !== 'expired' && a.decidedByName ? ` by ${a.decidedByName}` : '';
  return `${word}${by}${a.decidedAt ? `, ${when(a.decidedAt)}` : ''}`;
}

/** The run's flag for text that read like instructions (domain's UNTRUSTED_FLAG). */
export const UNTRUSTED_FLAG = 'instructions_in_untrusted_input';

/**
 * Whether the run that asked found instructions in untrusted text: the flag as data first; the words
 * that lead the request's explanation too (a run still going hasn't saved its flags yet).
 */
export const isFlagged = (a: Pick<Approval, 'explanation' | 'flags'>) =>
  !!a.flags?.includes(UNTRUSTED_FLAG) || !!a.explanation?.startsWith('Instructions found');

/** How long a request waits to tell what happened when an approved request ran. */
const FOLLOW_MS = [1000, 2000, 3000, 4000];

/**
 * After an approval, reads the request back until it has run, and says how it went (QA-859): the
 * worker runs it after the decision commits, so the decision's own answer can't say.
 */
export async function followApproved(
  id: string,
  agent: string,
  toast: (m: string) => void,
  after: () => void,
) {
  for (const ms of FOLLOW_MS) {
    await new Promise((r) => setTimeout(r, ms));
    const got = await client
      .call<{ approvals: Approval[] }>('approvals.list', { status: 'approved', limit: 20 })
      .catch(() => null);
    const text = approvedText(agent, got?.approvals.find((x) => x.id === id)?.outcome);
    if (text) {
      toast(text);
      after();
      return;
    }
  }
}
